import { useEffect, useRef } from 'react';
import { css, SerializedStyles } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { pendo } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import {
  Button,
  NumberField,
  Text,
  TextField,
  TextLink,
  useFormField,
  useOnClickOutside,
  useTooltip,
} from '@frontend/design-system';

type TextFieldConfig = {
  type?: 'text';
};

type NumberFieldConfig = {
  type: 'number';
  min?: number;
  max?: number;
  allowDecimal?: boolean;
  includeThousandsSeparator?: boolean;
};

export type FieldConfig = TextFieldConfig | NumberFieldConfig;

export interface EditableFieldProps {
  value: string;
  onSave: (newValue: string) => void;
  displayName: string;
  validatorFn: (value: string) => string;
  fieldId: string;
  isEditing: boolean;
  isSaving?: boolean;
  onStartEdit: (fieldId: string) => void;
  onCancelEdit: () => void;
  fieldConfig?: FieldConfig;
  helperText?: string;
  /** When true, validation errors are shown in a tooltip instead of inline below the field. */
  useTooltipError?: boolean;
  /** When true, a Cancel button is shown next to the input field. Defaults to true. */
  showCancelButton?: boolean;
  /** When true, the edit pencil icon is always visible instead of only on hover. */
  alwaysShowEditIcon?: boolean;
  trackingIdPrefix?: string;
  /** Additional styles applied to the outermost wrapper element. Use this to set a fixed width. */
  wrapperCss?: SerializedStyles;
}

export const EditableField = ({
  value,
  onSave,
  displayName,
  validatorFn,
  fieldId,
  isEditing,
  isSaving = false,
  onStartEdit,
  onCancelEdit,
  fieldConfig,
  helperText,
  useTooltipError = false,
  showCancelButton = true,
  alwaysShowEditIcon = false,
  trackingIdPrefix,
  wrapperCss,
}: EditableFieldProps) => {
  const fieldType = fieldConfig?.type ?? 'text';
  const { t } = useTranslation('phone', { keyPrefix: 'devices' });
  const wrapperRef = useRef<HTMLDivElement>(null);
  const isEditingRef = useRef(isEditing);
  isEditingRef.current = isEditing;

  const { Tooltip, triggerProps, tooltipProps, setOpen: setTooltipOpen } = useTooltip({ placement: 'top-end' });

  const fieldProps = useFormField(
    {
      type: fieldType,
      value,
      validator: ({ value: fieldValue }: { value: string }) => validatorFn(fieldValue),
      validateOnChange: true,
      ...fieldConfig,
    },
    [value, isEditing]
  );

  // Focus and select input text when entering edit mode
  useEffect(() => {
    if (isEditing && fieldProps.id) {
      const input = document.getElementById(fieldProps.id) as HTMLInputElement;
      if (input) {
        input.focus();
        input.select();
      }
    }
  }, [isEditing, fieldProps.id]);

  // Auto-show tooltip when there's a validation error (only when using tooltip errors)
  useEffect(() => {
    if (useTooltipError && isEditing) {
      setTooltipOpen(!!fieldProps.error);
    }
  }, [useTooltipError, isEditing, fieldProps.error, setTooltipOpen]);

  // Cancel the editing state if the user clicks outside the cell while editing (but not
  // while saving, to prevent interrupting the save flow)
  useOnClickOutside({
    active: isEditing && !isSaving,
    ref: wrapperRef,
    handler: () => onCancelEdit(),
  });

  const handleSave = async () => {
    const error = validatorFn(fieldProps.value);
    if (error) return;

    // If the value hasn't changed, just exit edit mode without calling onSave
    if (fieldProps.value === value) {
      onCancelEdit();
      return;
    }

    await onSave(fieldProps.value);
    if (isEditingRef.current) {
      onCancelEdit();
    }
  };

  // Use refs so the native listener always has the latest callbacks
  // without needing to re-attach on every render.
  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;
  const onCancelEditRef = useRef(onCancelEdit);
  onCancelEditRef.current = onCancelEdit;

  // Attach a native DOM keydown listener to stop propagation to the table's
  // native keydown listener (which React's synthetic stopPropagation cannot reach).
  // Only active while editing so table keyboard navigation works normally otherwise.
  useEffect(() => {
    const el = wrapperRef.current;
    if (!isEditing || !el) return;
    const handleNativeKeyDown = (e: KeyboardEvent) => {
      e.stopPropagation();
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSaveRef.current();

        if (trackingIdPrefix) {
          pendo.track(`${trackingIdPrefix}editable-field_save-enter-key`);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onCancelEditRef.current();

        if (trackingIdPrefix) {
          pendo.track(`${trackingIdPrefix}editable-field_cancel-escape-key`);
        }
      }
    };
    const stopNativeKeyUp = (e: KeyboardEvent) => e.stopPropagation();
    el.addEventListener('keydown', handleNativeKeyDown);
    el.addEventListener('keyup', stopNativeKeyUp);
    return () => {
      el.removeEventListener('keydown', handleNativeKeyDown);
      el.removeEventListener('keyup', stopNativeKeyUp);
    };
  }, [isEditing]);

  if (!isEditing) {
    return (
      <div
        className={`editable-field-${fieldId}`}
        css={[displayWrapperStyle(alwaysShowEditIcon), highlightStyle, wrapperCss]}
      >
        <Text css={textOverflowStyle}>{value}</Text>
        <Button
          iconName='edit'
          className='editable-field-edit-icon'
          variant='secondary'
          label={t('Edit')}
          trackingId={trackingIdPrefix ? `${trackingIdPrefix}editable-field_edit-btn` : 'editable-field_edit-btn'}
          onClick={(e) => {
            e.stopPropagation();
            onStartEdit(fieldId);
          }}
        />
      </div>
    );
  }

  const validationError = fieldProps.error;

  const fieldErrorProps = useTooltipError ? { hideErrorText: true } : {};

  const saveLink = (
    <TextLink
      size='large'
      onClick={() => handleSave()}
      disabled={!!validationError || !fieldProps.value || isSaving}
      trackingId={trackingIdPrefix ? `${trackingIdPrefix}editable-field_save-btn` : 'editable-field_save-btn'}
    >
      {t('Save')}
    </TextLink>
  );

  return (
    <div ref={wrapperRef} css={[editWrapperStyle, wrapperCss]} onClick={(e) => e.stopPropagation()}>
      <div css={editFieldRowStyle} {...(useTooltipError ? triggerProps : {})}>
        {fieldType === 'number' ? (
          <div css={fieldWrapperStyle}>
            <NumberField
              {...fieldProps}
              label={displayName}
              name={fieldId}
              disabled={isSaving}
              {...fieldErrorProps}
              helperText={helperText}
              endAdornment={saveLink}
            />
          </div>
        ) : (
          <div css={fieldWrapperStyle}>
            <TextField
              {...fieldProps}
              label={displayName}
              name={fieldId}
              disabled={isSaving}
              {...fieldErrorProps}
              helperText={helperText}
              endAdornment={saveLink}
            />
          </div>
        )}
        {showCancelButton && (
          <Button
            variant='tertiary'
            css={cancelButtonStyle}
            onClick={onCancelEdit}
            disabled={isSaving}
            trackingId={trackingIdPrefix ? `${trackingIdPrefix}editable-field_cancel-btn` : 'editable-field_cancel-btn'}
          >
            {t('Cancel')}
          </Button>
        )}
      </div>
      {useTooltipError && validationError && (
        <Tooltip {...tooltipProps}>
          <Text size='small' color='white'>
            {validationError}
          </Text>
        </Tooltip>
      )}
    </div>
  );
};

const displayWrapperStyle = (alwaysShowEditIcon: boolean) => css`
  display: flex;
  align-items: center;
  width: 100%;
  gap: ${theme.spacing(1)};

  .editable-field-edit-icon {
    visibility: ${alwaysShowEditIcon ? 'visible' : 'hidden'};
    flex-shrink: 0;
  }

  &:hover .editable-field-edit-icon {
    visibility: visible;
  }
`;

const textOverflowStyle = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const highlightStyle = css`
  border-radius: ${theme.borderRadius.small};
  box-shadow: 0 0 0 4px transparent;
  transition: background 0.8s ease-in-out, box-shadow 0.8s ease-in-out;

  &.highlighted {
    background: ${theme.colors.indigo10};
    box-shadow: 0 0 0 4px ${theme.colors.indigo10};
    transition: none;
  }
`;

const editWrapperStyle = css`
  width: 100%;
`;

const editFieldRowStyle = css`
  display: flex;
  align-items: start;
  gap: ${theme.spacing(1)};
`;

const fieldWrapperStyle = css`
  flex: 1;
  min-width: 0;

  & > * {
    width: 100%;
  }
`;

const cancelButtonStyle = css`
  margin-top: ${theme.spacing(0.75)};
  flex-shrink: 0;
`;
